import React from 'react'
import { OmDropdownMenu } from '@owma/kit'
import type { OmDropdownMenuConfig } from '@owma/kit'
import SortAscIcon from 'assets/images/chevron-down-24.svg?react'
import { getImageByRole } from 'pages/main/data/IconHelper'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmDelete } from 'utils/ui'
import { getSortOptions } from '../../../modules/TodoSort'
import styles from './style.module.scss'

interface TodoCompleteListHeaderProps {
  onRemoveCompletedTodo: (key: string) => void
  completedCount?: number
  currentFilter: string
  sortOption: string
  sortAscending?: boolean
  onSortDirectionToggle: () => void
  onSortChange?: (sortKey: string) => void
}

interface DeleteRangeLabels {
  [key: string]: string
}

const TodoCompleteListHeader: React.FC<TodoCompleteListHeaderProps> = ({
  onRemoveCompletedTodo,
  completedCount = 0,
  currentFilter,
  sortOption,
  sortAscending = true,
  onSortDirectionToggle,
  onSortChange: _onSortChange,
}) => {
  const { t } = useTranslation()
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false)

  // Sort-related functionality
  const sortOptions = getSortOptions(currentFilter as any)
  const currentSortOption = sortOptions.find((option) => option.key === sortOption)

  const handleSortDirectionToggle = (): void => {
    if (onSortDirectionToggle) {
      onSortDirectionToggle()
    }
  }

  const getDeleteRangeLabel = (key: string): string => {
    const rangeLabels: DeleteRangeLabels = {
      year: t('todo.todolist.deleteCompletedOptions.year'),
      month6: t('todo.todolist.deleteCompletedOptions.month6'),
      month3: t('todo.todolist.deleteCompletedOptions.month3'),
      month1: t('todo.todolist.deleteCompletedOptions.month1'),
    }
    return rangeLabels[key] || ''
  }

  const handleDeleteCompletedClick = (key: string, e?: React.MouseEvent | Event): void => {
    // Stop event propagation to prevent double triggering
    if (e) {
      e.stopPropagation()
    }

    const isDeleteAll = key === 'all'
    const deleteRangeLabel = getDeleteRangeLabel(key)

    const title = t('todo.todolist.deleteCompletedConfirm')
    const content = isDeleteAll
      ? t('todo.todolist.deleteAllCompletedDesc')
      : t('todo.todolist.deleteCompletedDesc', { delete_range: deleteRangeLabel })
    const confirmDesc = t('todo.todolist.deleteCompletedConfirmDesc')

    confirmDelete({
      title,
      content: (
        <div>
          <div>{content}</div>
          <div style={{ marginTop: '8px' }}>{confirmDesc}</div>
        </div>
      ),
      okText: t('common.button.delete'),
      cancelText: t('common.button.cancel'),
      onOk: () => {
        if (onRemoveCompletedTodo) {
          onRemoveCompletedTodo(key)
        }
      },
    })
  }

  const renderDeleteCompletedMenu = (): OmDropdownMenuConfig => {
    return {
      items: [
        { key: 'year', label: t('todo.todolist.deleteCompletedOptions.year') },
        { key: 'month6', label: t('todo.todolist.deleteCompletedOptions.month6') },
        { key: 'month3', label: t('todo.todolist.deleteCompletedOptions.month3') },
        { key: 'month1', label: t('todo.todolist.deleteCompletedOptions.month1') },
        { type: 'divider' as const },
        { key: 'all', label: t('todo.todolist.deleteCompletedOptions.all') },
      ],
      onClick: ({ key, domEvent }) => handleDeleteCompletedClick(key, domEvent as any),
    }
  }

  const deleteCompletedIcon = getImageByRole('trash', 'delete completed')
  const isDisabled = completedCount === 0

  return (
    <div className={styles.deleteCompletedHeader}>
      <div className={styles.deleteCompletedHeaderInner}>
        {/* Sort display section - Left side */}
        {currentSortOption && currentSortOption.label && (
          <div className={styles.sortSection}>
            <div className={styles.sortIconWrapper} onClick={handleSortDirectionToggle}>
              <SortAscIcon
                className={`${styles.sortIcon} ${!sortAscending ? styles.sortIconDesc : ''}`}
              />
            </div>
            <div className={styles.sortText} onClick={handleSortDirectionToggle}>
              {t('todo.todolist.sortBy', { sortBy: currentSortOption.label })}
            </div>
          </div>
        )}

        {/* Delete completed section - Right side */}
        <OmDropdownMenu
          menu={renderDeleteCompletedMenu()}
          trigger={isDisabled ? [] : ['click']}
          placement="bottomLeft"
          overlayClassName={'contextMenu'}
          open={isDisabled ? false : sortDropdownOpen}
          onOpenChange={isDisabled ? undefined : setSortDropdownOpen}
          disabled={isDisabled}
        >
          <div
            className={`${styles.deleteCompletedMenuWrapper} ${isDisabled ? styles.disabled : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              if (!isDisabled) {
                setSortDropdownOpen(!sortDropdownOpen)
              }
            }}
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            <div className={styles.deleteCompletedIconWrapper}>{deleteCompletedIcon}</div>
            <div className={styles.sortText}>{t('todo.todolist.deleteCompleted')}</div>
          </div>
        </OmDropdownMenu>
      </div>
    </div>
  )
}

export default TodoCompleteListHeader
